import ReactComment from './ReactComment';
import { OWA_AUTO_LINKS_CLASSNAME } from '../utils/constants';
import { observer } from 'mobx-react';
import { FileIcon, getIconForFile } from 'owa-file-icon';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Editor } from 'roosterjs-editor-core';
import {
    COMPOSE_ONLY_ATTACHMENT_CONTENT_END,
    COMPOSE_ONLY_ATTACHMENT_CONTENT_START,
} from 'owa-attachment-link-common';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';

const TEXT_DIRECTORY_MIME_TYPE = 'text/directory';
const ATTRIBUTE_CONTENTEDITABLE_NAME = 'contenteditable';

interface SharingLinkProps {
    displayName: string;
    url: string;
    fileIcon: string;
    editor: Editor;
    originalAnchor: HTMLAnchorElement;
    linkId: string;
}

const linkStyles = require('./Link.scss');
const classNames = require('classnames/bind').bind(linkStyles);

@observer
class Link extends React.Component<SharingLinkProps, {}> {
    private link: HTMLAnchorElement;

    render() {
        const conditionalStyles = {
            link: true,
        };

        return (
            <a
                ref={ref => (this.link = ref)}
                href={this.props.url}
                onMouseOver={this.onMouseOver}
                onMouseLeave={this.onMouseLeave}
                style={this.getAnchorStyle()}
                id={this.props.linkId}
                className={classNames(OWA_AUTO_LINKS_CLASSNAME, conditionalStyles)}>
                <img
                    src={this.props.fileIcon}
                    style={this.getFileIconStyle()}
                    alt=""
                    role="presentation"
                />
                {this.props.displayName}
                <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_START} />
                <Spinner className={linkStyles.linkSpinner} size={SpinnerSize.xSmall} />
                <ReactComment text={COMPOSE_ONLY_ATTACHMENT_CONTENT_END} />
            </a>
        );
    }

    componentDidMount() {
        this.link.setAttribute(ATTRIBUTE_CONTENTEDITABLE_NAME, 'false');

        // We need to replace the child only so we keep any text typed under the parent element before the new anchor
        this.props.editor.replaceNode(this.props.originalAnchor, this.link);
    }

    private getAnchorStyle(): React.CSSProperties {
        // Highlighted Background
        return {
            padding: '0 1px',
            backgroundColor: 'rgb(244,244,244)',
            borderRadius: '2px',
            userSelect: 'all',
        };
    }

    private getFileIconStyle() {
        return {
            width: '16px',
            height: '16px',
            verticalAlign: 'middle',
            padding: '1px 2px 2px 0',
        };
    }

    /*
    We have to use inline CSS style: background-color for it to work in different clients such as gmail.
    Since inline CSS style has higher preference than class, we can't use :hover as it would be just ignored.
    So we have to hook to these two events to change the hover background color.
    */

    private onMouseOver = () => {
        if (this.link) {
            this.link.style.backgroundColor = 'rgb(237,235,233)';
        }
    };

    private onMouseLeave = () => {
        if (this.link) {
            this.link.style.backgroundColor = 'rgb(243,242,241)';
        }
    };
}

export function createLoadingLinkView(
    editor: Editor,
    anchorElement: HTMLAnchorElement,
    url: string,
    linkId: string,
    fileName: string,
    mimeType: string
) {
    // to get the file icon, use the original name that came from the response, as the text filename could have been modified for display
    const fileIcon = getFileIcon(fileName, mimeType);

    const anchorContainer = document.createElement('span');

    ReactDOM.render(
        <Link
            displayName={fileName}
            fileIcon={fileIcon}
            editor={editor}
            originalAnchor={anchorElement}
            linkId={linkId}
            url={url}
        />,
        anchorContainer
    );
}

function getFileIcon(fileName: string, mimeType: string): string {
    const fileIcon: FileIcon = getIconForFile(
        fileName,
        mimeType === TEXT_DIRECTORY_MIME_TYPE,
        mimeType
    );
    return fileIcon.cdn || '';

    return '';
}
